import { useState, useRef, useEffect, type FormEvent, type KeyboardEvent } from 'react';
import { Spinner } from '../ui';
import type { Proposal } from './ProposalBubble';

interface ChatBottomBarProps {
  /** Режим: input для Real, status для Shadow */
  mode: 'input' | 'status';
  /** Отключён (чат не выбран) */
  disabled?: boolean;
  // Input mode props
  onSend?: (text: string) => void;
  isPending?: boolean;
  sendError?: Error | null;
  /**
   * Исход отправки, который не ошибка: например «такой ответ уже отправлен».
   *
   * Отдельно от `sendError`, потому что это не сбой — повтор не ушёл клиенту
   * намеренно, и человек должен узнать это словами, а не по тишине.
   */
  sendNotice?: string | null;
  placeholder?: string;
  // Status mode props
  proposals?: Proposal[];
}

export function ChatBottomBar({
  mode,
  disabled = false,
  onSend,
  isPending = false,
  sendError,
  sendNotice = null,
  placeholder = 'Сообщение... (Ctrl+Enter)',
  proposals = [],
}: ChatBottomBarProps): JSX.Element {
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (mode !== 'input') return;
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
    }
  }, [text, mode]);

  useEffect(() => {
    if (mode === 'input' && !disabled && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [disabled, mode]);

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (!text.trim() || isPending || disabled || !onSend) return;
    onSend(text.trim());
    setText('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (text.trim() && !isPending && !disabled && onSend) {
        onSend(text.trim());
        setText('');
      }
    }
  };

  // Disabled state — одинаковый для обоих режимов
  if (disabled) {
    return <div className="p-4 border-t border-border-subtle bg-surface-elevated min-h-[72px]" />;
  }

  // Input mode
  if (mode === 'input') {
    return (
      <form onSubmit={handleSubmit} className="p-4 border-t border-border-subtle bg-surface-elevated min-h-[72px]">
        {sendNotice && !sendError && (
          <div className="mt-1 text-xs text-accent-warning">{sendNotice}</div>
        )}
        {sendError && (
          <div className="mb-2 text-sm text-accent-danger">
            Ошибка: {sendError.message}
          </div>
        )}
        <div className="flex gap-2">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="flex-1 px-3 py-2 bg-surface-subtle border border-border-subtle rounded-lg resize-none text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-brand min-h-[40px] max-h-[150px]"
            rows={1}
            disabled={isPending}
          />
          <button
            type="submit"
            disabled={!text.trim() || isPending}
            className="px-4 py-2 bg-accent-brand text-white rounded-lg font-medium text-sm hover:bg-accent-brand/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors self-end"
          >
            {isPending ? (
              <Spinner size="sm" className="text-white" />
            ) : (
              'Отправить'
            )}
          </button>
        </div>
      </form>
    );
  }

  // Status mode (Shadow)
  const thinking = proposals.filter(p => p.status === 'thinking').length;
  const ready = proposals.filter(p => p.status === 'ready').length;
  const matched = proposals.filter(p => p.status === 'matched').length;
  const skipped = proposals.filter(p => p.status === 'skipped').length;

  return (
    <div className="p-4 border-t border-border-subtle bg-surface-elevated min-h-[72px]">
      <div className="flex items-center gap-3">
        {/* Thinking indicator */}
        {thinking > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-accent-warning/10 border border-accent-warning/20">
            <Spinner size="sm" className="text-accent-warning" />
            <span className="text-xs text-accent-warning">генерация...</span>
          </div>
        )}

        {/* Status badges */}
        <div className="flex items-center gap-2 text-xs text-text-secondary">
          {ready > 0 && (
            <span className="px-2 py-1 rounded bg-accent-success/10 text-accent-success">
              {ready} ready
            </span>
          )}
          {matched > 0 && (
            <span className="px-2 py-1 rounded bg-accent-brand/10 text-accent-brand">
              {matched} matched
            </span>
          )}
          {skipped > 0 && (
            <span className="px-2 py-1 rounded bg-surface-subtle text-text-secondary">
              {skipped} skip
            </span>
          )}
        </div>

        {/* Empty state */}
        {proposals.length === 0 && (
          <span className="text-xs text-text-secondary">
            Shadow ждёт сообщений клиента
          </span>
        )}
      </div>
    </div>
  );
}
